import React from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router';
import classNames from 'clsx';
import { ChevronRightRounded } from '@mui/icons-material';

import { channelPropTypes } from '../../../common/constants/propTypes';
import { MAIN_PLAYER_INSTANCE_KEY } from '@/modules/common/components/Player/constants';
import { VIEW_ALL } from '@/modules/common/components/Player/constants/customEvents';

import { usePlayerWidget } from '@/modules/common/components/Player/helpers';

import Carousel, { Skeleton as CarouselSkeleton } from '../Carousel/Carousel';
import CarouselLogo from '../CarouselLogo/CarouselLogo';
import { BrowserLink, Link, Stack, TruncatedText, Typography } from '@/mui/components';

import styles from './BackgroundCarousel.module.scss';

function BackgroundCarousel(props) {
  const location = useLocation();
  const LWP = usePlayerWidget(MAIN_PLAYER_INSTANCE_KEY);

  return (
    <div
      className={styles.SpecialIndent}
      data-a="channel-type-background"
      aria-label="Videos by channel"
      style={{
        '--sc-bg-color': props.bg,
        '--sc-logo-bg-color': props.bgLogo,
        '--sc-title-color': props.titleColor,
        '--sc-desc-color': props.descColor,
        '--sc-vid-title-color': props.vidTitleColor,
        '--sc-vid-info-color': props.vidInfoColor,
      }}
    >
      <div className={styles.Wrapper}>
        <div className={styles.Background} />
        <div className={styles.CarouselWrapper}>
          <div className={styles.ChannelInfoWrapper}>
            <div className={styles.ChannelInfo}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignContent="center"
                alignItems="center"
                className={styles.TitleWrapper}
                spacing={1}
              >
                <Link
                  data-a="channel-title"
                  color="inherit"
                  component={BrowserLink}
                  to={`/channel/${props.channel.id}${location.search}`}
                  className={styles.Title}
                >
                  <TruncatedText title={props.channel.title} rows={1}>
                    {props.channel.title}
                  </TruncatedText>
                </Link>
                <Link
                  data-a="channel-title-view-all"
                  color="inherit"
                  component={BrowserLink}
                  title={`All ${props.channel.title} videos`}
                  to={`/channel/${props.channel.id}${location.search}`}
                  onClick={() => {
                    LWP.sendCustomEvent(VIEW_ALL);
                  }}
                >
                  <Stack direction="row" alignItems="flex-end">
                    <Typography variant="body2" fontWeight={400} noWrap>
                      View All
                    </Typography>
                    <ChevronRightRounded fontSize="medium" />
                  </Stack>
                </Link>
              </Stack>
              <div className={styles.Description} data-a="channel-description">
                <TruncatedText title={props.channel.description} rows={2}>
                  {props.channel.description}
                </TruncatedText>
              </div>
            </div>
          </div>
          {props.channel.logo && (
            <div className={styles.Logo}>
              <CarouselLogo url={props.channel.logo} />
            </div>
          )}
          <div
            className={classNames(styles.Carousel, {
              [styles.Carousel___withLogo]: props.channel.logo,
            })}
          >
            {props.channel.playlist.length ? (
              <Carousel channel={props.channel} />
            ) : (
              <CarouselSkeleton channel={props.channel} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

BackgroundCarousel.propTypes = {
  bg: PropTypes.string.isRequired,
  bgLogo: PropTypes.string.isRequired,
  titleColor: PropTypes.string.isRequired,
  descColor: PropTypes.string.isRequired,
  vidTitleColor: PropTypes.string.isRequired,
  vidInfoColor: PropTypes.string.isRequired,
  channel: channelPropTypes.isRequired,
};

export default BackgroundCarousel;
